import moment from 'moment';
import {
  MAX_RATING_POINTS,
  CURRENCY_NAME,
  SCHEMA_TYPE,
  COUNTRY_NAME,
} from '@/constants/schema';
import { STORE_POLICY_PAGE_TYPE } from '@/constants/storePolicy';

const getReturnRefundPolicy = (data) => {
  const pageData = data?.find(
    (item) => item.page_type === STORE_POLICY_PAGE_TYPE.RETURN_AND_REFUND
  );
  return pageData ? pageData.additional_config_return_refund_policy : null;
};

const getVariantOption = (variantOption, val) => {
  for (const item of variantOption) {
    if (item?.option_name?.toLowerCase() === val) {
      return item?.variant_name;
    }
  }
  return false;
};

function getPriceValidUntil() {
  const currentDate = moment();
  return currentDate.add(1, 'year').format('YYYY-MM-DD');
}

export const getProductSchema = (props) => {
  const { product, storePolicy, storeUrl } = props;
  const productVariantsData = product?.variants;
  const reviewData = product?.store_item_review_rating;
  const returnAndRefundPolicy = getReturnRefundPolicy(storePolicy);

  const getTopRatedReview = (reviews) => {
    const topRatedReview =
      reviews?.length &&
      reviews?.reduce((topReview, currentReview) => {
        return currentReview?.rating > topReview?.rating ? currentReview : topReview;
      }, reviews[0]);
    return topRatedReview;
  };

  const getVariantsItemData = (variantsItemData) => {
    return variantsItemData?.map((item) => {
      const isColorVariant = getVariantOption(item?.option_variant_names, 'colour');
      const isSizeVariant = getVariantOption(item?.option_variant_names, 'size');
      const variantUrl = `${product?.link}?vid=${item?.variant_id}`;

      return {
        '@type': SCHEMA_TYPE.Product,
        ...(item?.sku_id && { sku: item.sku_id }),
        ...(item?.gst && { gtin14: item.gst }),
        image: item?.images?.[0]?.image_url || product?.image_url,
        name: `${product?.name} | ${item?.variant_name}`,
        ...(isColorVariant && { color: isColorVariant }),
        ...(isSizeVariant && { size: isSizeVariant }),
        offers: {
          '@type': SCHEMA_TYPE.Offer,
          url: variantUrl,
          priceCurrency: CURRENCY_NAME,
          price: item?.discounted_price,
          itemCondition: 'https://schema.org/NewCondition',
          availability: item?.available
            ? 'https://schema.org/InStock'
            : 'https://schema.org/OutOfStock',
          priceValidUntil: getPriceValidUntil(),
          ...(returnAndRefundPolicy?.is_accepting_returns && {
            hasMerchantReturnPolicy: {
              '@id': `${storeUrl}/policy`,
            },
          }),
        },
      };
    });
  };

  const topRatedReviewData = getTopRatedReview(reviewData?.review_rating);
  const isColorVariant = product?.options_show_type?.hasOwnProperty('Colour');
  const isSizeVariant = product?.options_show_type?.hasOwnProperty('Size');

  const productSchema = [
    {
      '@context': 'https://schema.org/',
      '@type': !!product?.variants_count ? SCHEMA_TYPE.ProductGroup : SCHEMA_TYPE.Product,
      name: product?.name,
      description: product?.description,
      ...(!product?.variants_count && {
        image: product?.image_url,
      }),
      url: product?.link,
      ...(product?.brand_name && {
        brand: {
          '@type': SCHEMA_TYPE.Brand,
          name: product?.brand_name,
        },
      }),
      ...(reviewData?.ratings_count &&
        topRatedReviewData?.full_name && {
          review: [
            {
              '@type': SCHEMA_TYPE.Review,
              reviewRating: {
                '@type': SCHEMA_TYPE.Rating,
                ratingValue: topRatedReviewData?.rating,
              },
              author: {
                '@type': 'Person',
                name: topRatedReviewData?.full_name,
              },
            },
          ],
        }),
      ...(reviewData?.ratings_count && {
        aggregateRating: {
          '@type': SCHEMA_TYPE.AggregateRating,
          ratingValue: reviewData?.review_rating?.[0]?.avg_rating,
          bestRating: MAX_RATING_POINTS,
          ratingCount: reviewData?.ratings_count,
        },
      }),
      ...(!product?.variants_count && {
        offers: {
          '@type': SCHEMA_TYPE.Offer,
          priceCurrency: CURRENCY_NAME,
          price: product?.discounted_price,
          itemCondition: 'https://schema.org/NewCondition',
          availability: 'https://schema.org/InStock',
          priceValidUntil: getPriceValidUntil(),
          ...(returnAndRefundPolicy?.is_accepting_returns && {
            hasMerchantReturnPolicy: {
              '@id': `${storeUrl}/policy`,
            },
          }),
        },
      }),
      ...((isColorVariant || isSizeVariant) && {
        variesBy: [
          ...(!!isColorVariant ? ['https://schema.org/Color'] : []),
          ...(!!isSizeVariant ? ['https://schema.org/Size'] : []),
        ],
      }),
      ...(product?.variants_count && {
        productGroupID: product?.id,
        hasVariant: getVariantsItemData(productVariantsData),
      }),
    },
  ];

  if (returnAndRefundPolicy && returnAndRefundPolicy?.is_accepting_returns) {
    const returnAndRefundPolicySchema = {
      '@context': 'http://schema.org/',
      '@type': SCHEMA_TYPE.MerchantReturnPolicy,
      '@id': `${storeUrl}/policy`,
      applicableCountry: COUNTRY_NAME,
      returnPolicyCategory: 'https://schema.org/MerchantReturnFiniteReturnWindow',
      merchantReturnDays: returnAndRefundPolicy?.day_count,
      returnMethod: 'https://schema.org/ReturnByMail',
      returnFees: 'https://schema.org/FreeReturn',
    };
    productSchema.push(returnAndRefundPolicySchema);
  }

  return productSchema;
};
